import { Alert, Box, Button, HStack } from '@chakra-ui/react'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { useState } from 'react'

import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useOffersAddonContext } from '@/context/OffersAddonProvider'
import { useOffersVehicleContext } from '@/context/OffersVehicleProvider'
import { usePrintContext } from '@/context/PrintContext'

import { SectionHeading } from '@/components/Section/SectionHeading'
import { BadgeStep } from '@/components/ui/BadgeStep'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import PoleasImportSummary from '@/pages/steps/vehicle/PoleasImportSummary'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { customAddDays } from 'BrokerTsLib/src/extensions/date.extensions'
import { Step2 } from '.'
import { Note } from '../../../../components/Note'
import { Steps } from '../../../../components/Steps'
import { WizzardHeader } from '../../../../components/WizzardHeader'
import { PageWrapper } from '../../../../layouts/components/PageWrapper'
import { Sidebar } from '../../../../layouts/components/Sidebar'
import { SendCalculationProvider } from '../../SendCalculationModal'

function Step2Wrapper() {
  const { app } = useApp()
  const [noteText, setNoteText] = useState<string | undefined>(undefined)
  const { insuranceParams: ip } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsDto

  const handleCreateNote = () => {
    setNoteText('Nová poznámka')
  }

  const useFilter = app != Apps.extra
  const isPublicPoleas = app == Apps.publicPoleas
  const { printMode } = usePrintContext()!

  const { offers } = useOffersVehicleContext()
  const { offers: addonOffers } = useOffersAddonContext()

  const offerIdsToPrint = offers.filter(x => x.print).map(x => x.offerId)
    .concat(addonOffers.filter(x => x.print).map(x => x.offerId))

  return (
    <SendCalculationProvider paramsId={insuranceParams!.generalParams.paramsId!} offerIds={offerIdsToPrint} editable={!isPublicPoleas}>
      <PageWrapper
        steps={!printMode && <Steps />}
        header={<WizzardHeader onCreateNote={handleCreateNote} />}
        sidebar={<Sidebar />}
        contentHeader={
          <>
            {!!insuranceParams?.importedData && !isPublicPoleas && <PoleasImportSummary />}
            <OutdatedOffersAlert />
          </>
        }
        contentProps={{
          sx: {
            pt: { base: 0, md: 0 },
          },
        }}
      >
        {noteText && noteText !== '' && (
          <Note
            date="11.10.2021 14:35"
            text={noteText}
            onChange={(text) => setNoteText(text)}
          />
        )}

        <Box sx={{ pt: { base: 0, md: !noteText && !printMode ? 8 : 0 } }}>
          <Step2 />
        </Box>
      </PageWrapper>
    </SendCalculationProvider>
  )
}

export default Step2Wrapper

const OutdatedOffersAlert = () => {
  const { translate } = useTranslationsContext()
  const { isContracted } = useInsuranceParams()
  const { offers, recalculateOffers, calculatingOffersIds } = useOffersVehicleContext()
  const { offers: addonOffers, recalculateOffers: recalculateAddonOffers, calculatingOffersIds: calculatingAddonOffersIds } = useOffersAddonContext()

  const anyOfferOutdated = !isContracted && [...offers, ...addonOffers].findIndex(x => x.dateCalculated && customAddDays(new Date, -1) > new Date(x.dateCalculated!)) >= 0

  if (!anyOfferOutdated) return null

  const isLoading = calculatingOffersIds.length > 0 || calculatingAddonOffersIds.length > 0

  return (
    <Alert colorScheme='orange'
      gap={2} px={8} py={4} mt={8}
      justifyContent='space-between'
    >
      <HStack gap={8}>
        <BadgeStep bg='orange.600'>!</BadgeStep>
        <SectionHeading sx={{ color: 'orange.900', fontWeight: 'semibold' }}>{translate('outdatedOffersWarning', 'Nabídky nemusí být aktuální')}</SectionHeading>
      </HStack>
      <Button
        ml={8}
        isLoading={isLoading}
        isDisabled={isLoading}
        colorScheme='orange'
        onClick={() => {
          recalculateOffers(offers.map(x => x.offerId), true)
          recalculateAddonOffers(addonOffers.map(x => x.offerId), true)
        }}>
        {translate('recalculate', 'Přepočítat')}
      </Button>
    </Alert>
  )
}